import { IconSparkles } from "@tabler/icons-react"
import { type FC, useEffect, useState } from "react"
import { useNavigate } from "react-router"
import styled from "styled-components"

import {
	aiGenerationCreditCosts,
	type ExplanationDepth,
	type LanguageProficiency,
	type Pacing,
	type Role,
	type SkillLevel,
	type Tone,
} from "@forento/core/models/ai"
import { toNumberFormat } from "@forento/core/utilities/number"

import Button, { SubmitButton } from "~/components/Button"
import Checkbox from "~/components/Checkbox"
import Dropdown from "~/components/Dropdown"
import InputField, { InputTextArea } from "~/components/InputField"
import {
	DefaultModal,
	ModalButtons,
	ModalContentPadding,
	ModalHeader,
	ModalInputs,
	ModalTitle,
} from "~/components/Modal"
import { useAlert } from "~/contexts/AlertContext"
import { usePlatform, useUser } from "~/contexts/UserContext"
import { captureForentoAiEvent, useForentoAiModalTracking } from "~/utilities/forentoAiAnalytics"
import routes from "~/utilities/routes"
import { query } from "~/utilities/trpc"

import HelpCenter from "../HelpCenter"
import AiGenerationStatus from "./AiGenerationStatus"
import Usage from "./Usage"

type Props = { isOpen: boolean; onBack(): void; onClose(): void }
const Ai: FC<Props> = ({ isOpen, onBack, onClose }) => {
	const alert = useAlert()
	const user = useUser()
	const platform = usePlatform()!
	const navigate = useNavigate()

	const [requestId, setRequestId] = useState<number | null>(null)

	const [title, setTitle] = useState("")
	const [contentToInclude, setContentToInclude] = useState("")
	const [instructions, setInstructions] = useState("")
	const [skillLevel, setSkillLevel] = useState<SkillLevel>("beginner")
	const [learningGoal, setLearningGoal] = useState("")
	const [role, setRole] = useState<Role>("student")
	const [languageProficiency, setLanguageProficiency] = useState<LanguageProficiency>("native")
	const [depthVsBreadth, setDepthVsBreadth] = useState(50)
	const [pacing, setPacing] = useState<Pacing>("balanced")
	const [tone, setTone] = useState<Tone>("motivational")
	const [explanationDepth, setExplanationDepth] = useState<ExplanationDepth>("conceptual")
	const [createFinalExam, setCreateFinalExam] = useState(false)
	const [includeQuizPages, setIncludeQuizPages] = useState(false)
	const [includeInputPages, setIncludeInputPages] = useState(false)

	const aiStatus = query.ai.getCourseStatus.useQuery(requestId ?? -1, {
		enabled: requestId !== null,
		refetchInterval: query =>
			query.state.data !== null &&
			query.state.data?.status !== "completed" &&
			query.state.data?.status !== "error"
				? 3000
				: false,
	})

	const create = query.ai.createCourse.useMutation({
		async onError(error) {
			console.error(error)
			captureForentoAiEvent("forento_ai_generation_failed", { type: "course" })
			await alert.show(
				"Failed to create course with AI",
				"An error occurred while creating the course with AI. Please try again.",
			)
		},
		onSuccess(data) {
			setRequestId(data.requestId)
		},
	})

	const isCompleted = aiStatus.data?.status === "completed"
	const isFailed = aiStatus.data?.status === "error"
	const isFinished = isCompleted || isFailed

	useEffect(() => {
		if (!isCompleted) return
		captureForentoAiEvent("forento_ai_generation_succeeded", { type: "course" })
		void user.reload()
	}, [isCompleted])

	useEffect(() => {
		if (isFailed) captureForentoAiEvent("forento_ai_generation_failed", { type: "course" })
	}, [isFailed])

	const courseCost = aiGenerationCreditCosts.course
	const hasEnoughCredits = platform.aiCreditBalance >= courseCost
	useForentoAiModalTracking({
		type: "course",
		isFormVisible: isOpen && requestId === null,
		creditBalance: platform.aiCreditBalance,
		cost: courseCost,
	})
	const canGenerate = title.trim().length > 0 && hasEnoughCredits && !create.isPending

	const handleGenerate = () => {
		if (!canGenerate) return
		captureForentoAiEvent("forento_ai_generation_submitted", { type: "course" })
		create.mutate({
			title,
			contentToInclude,
			instructions,
			skillLevel,
			learningGoal,
			role,
			languageProficiency,
			depthVsBreadth,
			pacing,
			tone,
			explanationDepth,
			createFinalExam,
			includeQuizPages,
			includeInputPages,
		})
	}

	if (requestId !== null) {
		const statusData = aiStatus.data
		const courseId = statusData?.status === "completed" ? statusData.courseId : null
		const handleClose = () => {
			// Closing while the worker is still generating keeps the request id, since
			// dropping it stops the status query and the balance the worker charges would
			// never be reloaded.
			if (isFinished) {
				setRequestId(null)
				setTitle("")
				setContentToInclude("")
				setSkillLevel("beginner")
				setLearningGoal("")
				setRole("student")
				setLanguageProficiency("native")
				setDepthVsBreadth(50)
				setPacing("balanced")
				setTone("motivational")
				setExplanationDepth("conceptual")
				setCreateFinalExam(false)
				setIncludeQuizPages(false)
				setIncludeInputPages(false)
				setInstructions("")
			}
			onClose()
		}

		return (
			<DefaultModal width={640} isOpen={isOpen} onClose={handleClose}>
				<ModalHeader>
					<ModalTitle>{isCompleted ? "Course generated" : "Generating your course"}</ModalTitle>
					<HelpCenter />
				</ModalHeader>
				<ModalContentPadding>
					<AiGenerationStatus requestId={requestId} />
				</ModalContentPadding>
				<ModalButtons>
					<Button variant="secondary" onClick={handleClose}>
						Close
					</Button>
					{courseId !== null && (
						<>
							<Button
								variant="secondary"
								onClick={() => navigate(`${routes.course.detail(courseId)}?ai=true`)}
							>
								<IconSparkles className="size-4" />
								Refine with Forento AI
							</Button>
							<Button variant="primary" onClick={() => navigate(routes.course.detail(courseId))}>
								Open course
							</Button>
						</>
					)}
				</ModalButtons>
			</DefaultModal>
		)
	}

	return (
		<DefaultModal
			width={1000}
			isOpen={isOpen}
			onSubmit={handleGenerate}
			onClose={create.isPending ? undefined : onBack}
		>
			<ModalHeader>
				<ModalTitle>Generate a new course with Forento AI</ModalTitle>
				<HelpCenter />
			</ModalHeader>
			<ModalInputs>
				<div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
					<p className="text-sm text-amber-900">
						<span className="font-semibold">Forento AI is in beta.</span> Results may vary and you may need
						to edit the generated content.
					</p>
				</div>
				<Usage />
				<InputField label="Suggested title" value={title} onChange={setTitle} />
				<InputField
					label="Learning goal"
					value={learningGoal}
					onChange={setLearningGoal}
					placeholder="e.g., get a job, pass an exam, build a project"
				/>
				<InputTextArea
					label="Content to include"
					value={contentToInclude}
					onChange={setContentToInclude}
					placeholder="List specific concepts, topics, or content you want covered in the course"
				/>
				<ThreeColumnRow>
					<Dropdown
						label="Skill level"
						items={[
							{ id: "beginner", title: "Beginner" },
							{ id: "intermediate", title: "Intermediate" },
							{ id: "advanced", title: "Advanced" },
							{ id: "mixed", title: "Mixed" },
						]}
						selectedItemId={skillLevel}
						onChange={id => setSkillLevel(id as typeof skillLevel)}
					/>
					<Dropdown
						label="Role"
						items={[
							{ id: "student", title: "Student" },
							{ id: "professional", title: "Professional" },
							{ id: "manager", title: "Manager" },
							{ id: "researcher", title: "Researcher" },
							{ id: "hobbyist", title: "Hobbyist" },
						]}
						selectedItemId={role}
						onChange={id => setRole(id as typeof role)}
					/>
					<Dropdown
						label="Language"
						items={[
							{ id: "native", title: "Native" },
							{ id: "non-native", title: "Non-native" },
							{ id: "simplified", title: "Simplified" },
						]}
						selectedItemId={languageProficiency}
						onChange={id => setLanguageProficiency(id as typeof languageProficiency)}
					/>
				</ThreeColumnRow>
				<ThreeColumnRow>
					<Dropdown
						label="Pacing"
						items={[
							{ id: "slow-and-thorough", title: "Slow" },
							{ id: "balanced", title: "Balanced" },
							{ id: "fast-and-dense", title: "Fast" },
						]}
						selectedItemId={pacing}
						onChange={id => setPacing(id as typeof pacing)}
					/>
					<Dropdown
						label="Tone"
						items={[
							{ id: "formal", title: "Formal" },
							{ id: "conversational", title: "Conversational" },
							{ id: "motivational", title: "Motivational" },
							{ id: "strict", title: "Strict/Academic" },
						]}
						selectedItemId={tone}
						onChange={id => setTone(id as typeof tone)}
					/>
					<Dropdown
						label="Explanation depth"
						items={[
							{ id: "intuitive", title: "Intuitive" },
							{ id: "conceptual", title: "Conceptual" },
							{ id: "mathematical", title: "Mathematical" },
						]}
						selectedItemId={explanationDepth}
						onChange={id => setExplanationDepth(id as typeof explanationDepth)}
					/>
				</ThreeColumnRow>
				<div>
					<div className="mb-1 flex items-center justify-between">
						<label className="block text-sm font-medium text-gray-900">Depth vs Breadth</label>
						<div className="flex items-center gap-2">
							<span className="text-sm font-semibold text-gray-700">
								{depthVsBreadth < 33 ? "Deep dive" : depthVsBreadth < 67 ? "Balanced" : "Overview"}
							</span>
							<span className="rounded-md bg-gray-100 px-2 py-0.5 text-sm font-medium text-gray-700">
								{depthVsBreadth}
							</span>
						</div>
					</div>
					<input
						type="range"
						min="0"
						max="100"
						value={depthVsBreadth}
						onChange={e => setDepthVsBreadth(Number(e.target.value))}
						className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200"
					/>
					<div className="mt-0.5 flex justify-between text-xs text-gray-500">
						<span>Deep dive</span>
						<span>Overview</span>
					</div>
				</div>
				<div className="rounded-lg border border-gray-200 bg-white p-3">
					<Checkbox label="Create a final exam" isChecked={createFinalExam} onChange={setCreateFinalExam} />
					<p className="mt-1 pl-7 text-sm text-gray-500">
						Multiple-choice questions based on all generated lessons.
					</p>
				</div>
				<div className="rounded-lg border border-gray-200 bg-white p-3">
					<p className="mb-3 text-sm font-semibold text-gray-900">Page types</p>
					<div className="flex flex-col gap-3">
						<div>
							<Checkbox
								label="Include quiz pages"
								isChecked={includeQuizPages}
								onChange={setIncludeQuizPages}
							/>
							<p className="mt-1 pl-7 text-sm text-gray-500">
								Short multiple-choice checks between lesson pages.
							</p>
						</div>
						<div>
							<Checkbox
								label="Include input pages"
								isChecked={includeInputPages}
								onChange={setIncludeInputPages}
							/>
							<p className="mt-1 pl-7 text-sm text-gray-500">
								Free-text reflection or application prompts for students.
							</p>
						</div>
					</div>
				</div>
				<InputTextArea label="Additional instructions" value={instructions} onChange={setInstructions} />
			</ModalInputs>
			<ModalButtons>
				<Button variant="secondary" onClick={onBack}>
					Back
				</Button>
				<SubmitButton variant="primary" isDisabled={!canGenerate} isLoading={create.isPending}>
					Generate with Forento AI ({toNumberFormat(courseCost)} credits)
				</SubmitButton>
			</ModalButtons>
		</DefaultModal>
	)
}

const ThreeColumnRow = styled.div`
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 12px;
`

export default Ai
